import React, { useState, useCallback, useEffect, Suspense } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Box,
  Avatar,
  Typography,
  Button,
  Container,
  CircularProgress,
} from "@mui/material";
//import Transactions from "./TransactionsPage";//2ndJuly 2026 changed to Lazy Import
import ErrorBoundary from "../components/ErrorBoundary";
import logo from "../images/amaple.jpg";
//import axiosInstance from "../services/api";
import { getFirmDetails } from "../services/api"; //25Aug2025
const Transactions = React.lazy(() => import("./TransactionsPage"));
const FinancialReports = React.lazy(() => import("./FinancialReportsPage"));
const AssetsAndExpenses = React.lazy(() => import("./AssetsAndExpensesPage"));
//const Communications = React.lazy(() => import("./CommunicationsPage"));
const HRCommunications = React.lazy(() => import("./HRCommunicationsPage"));
const Patients = React.lazy(() => import("./PatientsPage"));
const Equity = React.lazy(() => import("./EquityPage"));

const Dashboard = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [firmData, setFirmData] = useState(null); //25Aug2025
  const navigate = useNavigate();
  const { userData } = useOutletContext(); // Secure role source
  //updates on 28Th Aug reagrding avatar
  // Avatar URL function
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;

    // If it's already a full URL (from Cloudinary), use it directly
    if (avatarPath.startsWith("http")) {
      return avatarPath;
    }

    // In development - use local Django server
    if (process.env.NODE_ENV === "development") {
      return `http://127.0.0.1:8000${avatarPath}`;
    }

    // In production - use current tenant's subdomain (not API domain)
    const hostname = window.location.hostname;
    return `https://${hostname}${avatarPath}`;
  };
  // 25Aug2025 Fetch firm data if employment data is missing
  // Add this ref at the top of the Dashboard component (after useState declarations)
  const hasFetchedFirm = React.useRef(false);

  useEffect(() => {
    // Only run once per component lifetime
    if (hasFetchedFirm.current) return;
    hasFetchedFirm.current = true;

    if (userData?.employment?.firm) {
      setFirmData(userData.employment.firm);
      return;
    }

    // If employment data is missing, fetch directly
    getFirmDetails()
      .then(setFirmData)
      .catch(() =>
        setFirmData({
          name: "MedicERP Pharmacy",
          address: "123 Business St, City",
        }),
      );
  }, [userData]); // ← firmData REMOVED from deps
  //Aug25 added this block upto here//
  // Role-based tabs
  const roleTabs = {
    system_admin: [
      "Products",
      "Assets & Expenses",
      "Financial Reports",
      "Equity",
      "CR & PM",
      "HR & Com",
    ],
    system_manager: [
      "Products",
      "Assets & Expenses",
      "Financial Reports",
      "Equity",
      "CR & PM",
      "HR & Com",
    ],
    system_user: ["Products", "Financial Reports", "CR & PM", "HR & Com"],
  };

  const userRole = userData?.system_role?.toLowerCase() || "system_user";
  const tabs = roleTabs[userRole] || [];

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate("/login");
  }, [navigate]);

  // Temporarily add this at the start of your Dashboard return statement:
  //console.log('Rendering Dashboard with:', { userData, userRole, tabs });//2nd July, 2026
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top Navigation Bar */}
      <AppBar position="static" sx={{ bgcolor: "primary.main", p: 1 }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* ERP Name, Logo & Firm Name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <img
              src={logo}
              alt="MedicERP Logo"
              style={{ height: "40px", width: "40px", borderRadius: "50%" }}
            />
            <Typography variant="h6" fontWeight="bold">
              MedicERP
            </Typography>
            <Box
              sx={{ display: "flex", flexDirection: "column", marginLeft: 2 }}
            >
              <Typography variant="h6" fontWeight="bold">
                {/*{userData.employment?.firm?.name || "Firm Name"} 25Th Aug*/}
                {firmData?.name ||
                  userData?.employment?.firm?.name ||
                  "Firm Name"}
              </Typography>
              <Typography variant="body2">
                {firmData?.address ||
                  userData?.employment?.firm?.address ||
                  "123 Business St, City"}
              </Typography>
            </Box>
          </Box>

          {/* User Avatar & Logout */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Avatar
                src={userData.avatar ? getAvatarUrl(userData.avatar) : null}
                sx={{ bgcolor: "secondary.main" }}
                onError={(e) => {
                  // Hide broken images and let the fallback initials show
                  e.target.style.display = "none";
                }}
              >
                {userData.username[0]}
              </Avatar>
              <Typography variant="caption">
                {userData.employment?.name}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleLogout}
              >
                Logout
              </Button>
              <Typography variant="caption">({userRole})</Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      {/* DEBUG SECTION
        <Box sx={{ p: 2, bgcolor: 'yellow' }}>
        <Typography variant="h6">Debug Info:</Typography>
        <pre>{JSON.stringify(userData, null, 2)}</pre>
        <pre>{JSON.stringify(userData.employment.firm, null, 2)}</pre>
        <Typography>Active Tab: {tabs[tabIndex]}</Typography>
        </Box> 
        */}

      {/* Rest of your dashboard remains the same */}
      {/* Tabs Section 
       <Container sx={{ mt: 4 }}>*/}
      <Container maxWidth={false} disableGutters sx={{ mt: 4, px: 1 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newIndex) => setTabIndex(newIndex)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab} />
          ))}
        </Tabs>

        {/* Render Tab Content */}
        <Box sx={{ mt: 3, p: 3, border: "1px solid #ddd", borderRadius: 2 }}>
          {(() => {
            const activeTabName = tabs[tabIndex];

            // Products / Transactions (same behavior as before)
            if (activeTabName === "Products") {
              return (
                <Suspense
                  fallback={
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography>Loading transactions...</Typography>
                    </Box>
                  }
                >
                  <Transactions userRole={userRole} />
                </Suspense>
              );
            }

            // Financial Reports -> render the page (pass any props you need)
            if (activeTabName === "Financial Reports") {
              return (
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <CircularProgress />
                        <Typography>Loading financial reports...</Typography>
                      </Box>
                    }
                  >
                    <FinancialReports
                      userRole={userRole}
                      userData={userData}
                      firmData={firmData}
                    />
                  </Suspense>
                </ErrorBoundary>
              );
            }
            // Add these cases to your existing switch statement
            if (activeTabName === "Assets & Expenses") {
              return (
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <CircularProgress />
                        <Typography>Loading Assets & Expenses...</Typography>
                      </Box>
                    }
                  >
                    <AssetsAndExpenses
                      userRole={userRole}
                      userData={userData}
                      firmData={firmData}
                    />
                  </Suspense>
                </ErrorBoundary>
              );
            }

            if (activeTabName === "HR & Com") {
              return (
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <CircularProgress />
                        <Typography>Loading HR Management...</Typography>
                      </Box>
                    }
                  >
                    <HRCommunications
                      userRole={userRole}
                      userData={userData}
                      firmData={firmData}
                    />
                  </Suspense>
                </ErrorBoundary>
              );
            }

            if (activeTabName === "CR & PM") {
              return (
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <CircularProgress />
                        <Typography>Loading Patient Management..</Typography>
                      </Box>
                    }
                  >
                    <Patients
                      userRole={userRole}
                      userData={userData}
                      firmData={firmData}
                    />
                  </Suspense>
                </ErrorBoundary>
              );
            }
            if (activeTabName === "Equity") {
              return (
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <CircularProgress />
                        <Typography>Loading Equity Management..</Typography>
                      </Box>
                    }
                  >
                    <Equity
                      userRole={userRole}
                      userData={userData}
                      firmData={firmData}
                    />
                  </Suspense>
                </ErrorBoundary>
              );
            }

            // Default for other tabs
            return (
              <Box textAlign="center">
                <Typography variant="h6">{activeTabName}</Typography>
                <Typography color="textSecondary">
                  {userRole === "system_user"
                    ? "Contact your manager for access"
                    : `Content for ${activeTabName} goes here...`}
                </Typography>
              </Box>
            );
          })()}
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
